import { useEffect } from "react";
import router, { useRouter } from "next/router";
import { useTranslation } from "next-i18next";

import { Tabs } from "common/elements/Tabs";
import { Table } from "common/ui/Table";
import { PublishedListingsTable } from "../PublishedListingsTable";
import { DraftListingsTable } from "../DraftListingsTable";
import { ArchivedListingsTable } from "../ArchivedListingsTable";

import { ListingLookupDto } from "common/middleware/api.services";

export interface ListingTablesProps {
  listings: ListingLookupDto[],
  numOfDraftListings: number,
  numOfPublishedListings: number,
  setListingLink: (value: string) => void,
  setIdOfListingToDelete: (value: number) => void,
  setIdOfListingToArchive: (propertyId: number) => void,
  isLoading?: boolean
}

export function ListingTables({
  listings,
  numOfDraftListings,
  numOfPublishedListings,
  setListingLink,
  setIdOfListingToDelete,
  setIdOfListingToArchive,
  isLoading
}: ListingTablesProps) {
  const { query, isReady } = useRouter();
  const { t } = useTranslation("admin");

  const items = [
    ...(numOfPublishedListings > 0 ? [{
      onClick: () => router.replace({
        query: { ...query, view: "published" }
      }),
      header: t("listingTables.published", { num: numOfPublishedListings }),
      id: "published"
    }] : []),
    ...(numOfDraftListings > 0 ? [{
      onClick: () => router.replace({
        query: { ...query, view: "draft" }
      }),
      header: t("listingTables.draft", { num: numOfDraftListings }),
      id: "draft"
    }] : []),
    {
      onClick: () => router.replace({
        query: { ...query, view: "archived" }
      }),
      header: t("listingTables.archived"),
      id: "archived"
    }
  ];

  useEffect(() => {
    if (!isReady
      || query.view
      || numOfDraftListings === undefined
      || numOfPublishedListings === undefined
    ) return;

    const view = numOfPublishedListings > 0
      ? "published"
      : numOfDraftListings > 0
        ? "draft"
        : "archived";

    if (query.view !== view) {
      router.replace(
        { query: { ...query, view: view } },
        undefined,
        { shallow: true }
      );
    }
  }, [query, isReady, numOfPublishedListings, numOfDraftListings]);

  return (
    <div>
      <Tabs
        items={items}
        selectedId={query.view as string}
        isLoading={isLoading}
      />
      {query.view === "published" ? (
        <PublishedListingsTable
          listings={listings}
          setListingLink={setListingLink}
        />
      ) : query.view === "draft" ? (
        <DraftListingsTable
          listings={listings?.filter((l) => !l.archived)}
          setIdOfListingToDelete={setIdOfListingToDelete}
          setIdOfListingToArchive={setIdOfListingToArchive}
        />
      ) : query.view === "archived" ? (
        <ArchivedListingsTable
          listings={listings?.filter((l) => l.archived)}
        />
      ) :
        <Table
          rows={undefined}
          headers={["", "", "", ""]}
        />
      }
    </div>
  )
};
